/**
 * API Client for Admin Dashboard
 * Handles JWT token management, refresh, and API calls
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8002/api';

// Keys DRF uses for a whole-request error rather than a field.
const NON_FIELD_KEYS = ['detail', 'message', 'error', 'non_field_errors'];

/**
 * Field-level errors from a DRF error body, as { field: "message" }.
 *
 * DRF reports validation per field - {"department": ["\"management\" is not a
 * valid choice."]} - with no `detail` key anywhere.
 */
function fieldErrorsFrom(errorData) {
  if (!errorData || typeof errorData !== 'object' || Array.isArray(errorData)) return {};

  return Object.entries(errorData).reduce((fields, [key, value]) => {
    if (NON_FIELD_KEYS.includes(key)) return fields;
    const text = Array.isArray(value) ? value.join(' ') : value;
    if (typeof text === 'string' && text) fields[key] = text;
    return fields;
  }, {});
}

/**
 * A message worth showing a user, from any shape DRF returns.
 *
 * Reading only `detail`/`message`/`error` meant every field-level validation
 * failure fell through to a bare "HTTP 400" - the status and nothing else. A
 * cost-centre department the form could never satisfy, and a route-price
 * priority sent as a string to an integer column, both surfaced as that same
 * four-character message, which is why neither was diagnosable from the UI.
 */
function describeApiError(errorData, status) {
  if (typeof errorData === 'string' && errorData) return errorData;

  for (const key of NON_FIELD_KEYS) {
    const value = errorData?.[key];
    if (typeof value === 'string' && value) return value;
    if (Array.isArray(value) && value.length) return value.join(' ');
  }

  const fields = fieldErrorsFrom(errorData);
  const entries = Object.entries(fields);
  if (entries.length) {
    return entries.map(([field, text]) => `${field}: ${text}`).join(' — ');
  }

  return `HTTP ${status}`;
}

class AdminAPIClient {
  constructor() {
    this.refreshPromise = null;
  }

  /**
   * Get access token from localStorage (always read fresh)
   */
  getAccessTokenSync() {
    return localStorage.getItem('admin_access_token');
  }

  /**
   * Get refresh token from localStorage (always read fresh)
   */
  getRefreshTokenSync() {
    return localStorage.getItem('admin_refresh_token');
  }

  /**
   * Get access token from localStorage or refresh if needed
   */
  async getAccessToken() {
    // Always read from localStorage to ensure we have the latest token
    let accessToken = this.getAccessTokenSync();
    const refreshToken = this.getRefreshTokenSync();
    
    // Check if token exists and is valid
    if (accessToken) {
      // Decode JWT to check expiration
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const exp = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();
        
        // If token expires in less than 2 minutes, refresh it proactively
        if (exp - now < 2 * 60 * 1000) {
          if (refreshToken) {
            accessToken = await this.refreshAccessToken();
            return accessToken;
          }
        }
        
        // Token is still valid
        return accessToken;
      } catch (e) {
        // If decoding fails, try to refresh
        if (refreshToken) {
          accessToken = await this.refreshAccessToken();
          return accessToken;
        }
        // Clear invalid token
        localStorage.removeItem('admin_access_token');
        return null;
      }
    }

    if (refreshToken) {
      accessToken = await this.refreshAccessToken();
      return accessToken;
    }

    return null;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken() {
    // If already refreshing, wait for that promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Always read refresh token fresh from localStorage
    const refreshToken = this.getRefreshTokenSync();
    if (!refreshToken) {
      this.logout();
      throw new Error('No refresh token available');
    }

    this.refreshPromise = fetch(`${API_BASE_URL}/auth/token/refresh/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    })
      .then(async response => {
        if (!response.ok) {
          // If refresh fails, clear tokens
          this.logout();
          throw new Error('Token refresh failed');
        }
        return response.json();
      })
      .then(data => {
        if (data.access) {
          // Store new access token
          localStorage.setItem('admin_access_token', data.access);
          // If new refresh token is provided, update it
          if (data.refresh) {
            localStorage.setItem('admin_refresh_token', data.refresh);
          }
          this.refreshPromise = null;
          return data.access;
        } else {
          // Refresh failed - clear tokens and redirect to login
          this.logout();
          throw new Error('Token refresh failed - no access token received');
        }
      })
      .catch(error => {
        this.refreshPromise = null;
        // Only logout if it's an authentication error
        if (error.message.includes('refresh') || error.message.includes('Token')) {
          this.logout();
        }
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Make authenticated API request with automatic token refresh
   */
  async request(endpoint, options = {}) {
    const token = await this.getAccessToken();
    
    if (!token && endpoint !== '/auth/token/' && endpoint !== '/auth/register/') {
      throw new Error('Not authenticated');
    }

    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add tenant ID if available (from subdomain or token)
    const tenantId = this.getTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }

    let response;
    try {
      response = await fetch(url, {
        ...options,
        headers,
      });
    } catch (error) {
      // Network error
      throw new Error(`Network error: ${error.message}`);
    }

    // Handle 401 - try to refresh token
    if (response.status === 401 && this.getRefreshTokenSync()) {
      try {
        const newToken = await this.refreshAccessToken();
        // Retry request with new token
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...options,
          headers,
        });
      } catch (refreshError) {
        // Refresh failed - redirect to login
        this.logout();
        throw new Error('Authentication expired. Please login again.');
      }
    }

    // Handle errors
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { detail: `HTTP ${response.status}: ${response.statusText}` };
      }
      const error = new Error(describeApiError(errorData, response.status));
      // The per-field detail, for forms that can highlight the offending
      // input rather than only showing the combined message.
      error.fieldErrors = fieldErrorsFrom(errorData);
      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  /**
   * Get tenant ID from subdomain or token
   */
  getTenantId() {
    // In production, extract from subdomain or JWT token
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      // e.g., tenant-id.fleet.indexaspire.com
      return parts[0];
    }
    // For development, try localStorage
    return localStorage.getItem('default_tenant_id');
  }

  /**
   * Login and store tokens
   */
  async login(username, password) {
    const response = await this.request('/auth/token/', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });

    if (response.access && response.refresh) {
      // Store tokens in localStorage (always read from here, never cache in instance)
      localStorage.setItem('admin_access_token', response.access);
      localStorage.setItem('admin_refresh_token', response.refresh);
      return response;
    }

    throw new Error('Login failed');
  }

  /**
   * Logout and clear tokens
   */
  logout() {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    // Clear any pending refresh promise
    this.refreshPromise = null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    const token = this.getAccessTokenSync();
    if (!token) return false;
    
    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      const now = Date.now();
      return exp > now; // Token is valid if expiration is in the future
    } catch (e) {
      return false; // Invalid token format
    }
  }

  // ============ Fleet Management API Methods ============

  /**
   * Get vehicles list
   */
  async getVehicles(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/fleet/vehicles/?${queryString}` : '/fleet/vehicles/';
    return this.request(endpoint);
  }

  /**
   * List pricing plans. Booking.pricing_plan is required by the serializer, so
   * the booking form cannot submit without one of these.
   */
  async getPricingPlans(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/pricing-plans/?${queryString}` : '/bookings/pricing-plans/';
    return this.request(endpoint);
  }

  /**
   * Get vehicle detail
   */
  async getVehicle(id) {
    return this.request(`/fleet/vehicles/${id}/`);
  }

  /**
   * Get drivers list
   */
  async getDrivers(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/organization/drivers/?${queryString}` : '/organization/drivers/';
    return this.request(endpoint);
  }

  /**
   * Create vehicle
   */
  async createVehicle(data) {
    return this.request('/fleet/vehicles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update vehicle
   */
  async updateVehicle(id, data) {
    return this.request(`/fleet/vehicles/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete vehicle
   */
  async deleteVehicle(id) {
    return this.request(`/fleet/vehicles/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Get vehicle purchase transactions
   */
  async getVehiclePurchases(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/fleet/vehicle-purchases/?${queryString}` : '/fleet/vehicle-purchases/';
    return this.request(endpoint);
  }

  /**
   * Get vehicle purchase detail
   */
  async getVehiclePurchase(id) {
    return this.request(`/fleet/vehicle-purchases/${id}/`);
  }

  /**
   * Create vehicle purchase transaction
   */
  async createVehiclePurchase(data) {
    return this.request('/fleet/vehicle-purchases/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update vehicle purchase transaction
   */
  async updateVehiclePurchase(id, data) {
    return this.request(`/fleet/vehicle-purchases/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete vehicle purchase transaction
   */
  async deleteVehiclePurchase(id) {
    return this.request(`/fleet/vehicle-purchases/${id}/`, {
      method: 'DELETE',
    });
  }

  // ============ Investor Management API Methods ============

  /**
   * Get investors list
   */
  async getInvestors(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/organization/investors/?${queryString}` : '/organization/investors/';
    return this.request(endpoint);
  }

  /**
   * Get investor detail
   */
  async getInvestor(id) {
    return this.request(`/organization/investors/${id}/`);
  }

  /**
   * Create investor
   */
  async createInvestor(data) {
    return this.request('/organization/investors/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update investor
   */
  async updateInvestor(id, data) {
    return this.request(`/organization/investors/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete investor
   */
  async deleteInvestor(id) {
    return this.request(`/organization/investors/${id}/`, {
      method: 'DELETE',
    });
  }

  // ============ Booking Management API Methods ============

  /**
   * Get bookings list
   */
  async getBookings(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/bookings/?${queryString}` : '/bookings/bookings/';
    return this.request(endpoint);
  }

  /**
   * Get booking detail
   */
  async getBooking(id) {
    return this.request(`/bookings/bookings/${id}/`);
  }

  /**
   * Create booking
   */
  async createBooking(data) {
    return this.request('/bookings/bookings/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update booking
   */
  async updateBooking(id, data) {
    return this.request(`/bookings/bookings/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete booking
   */
  async deleteBooking(id) {
    return this.request(`/bookings/bookings/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Assign vehicle to booking
   */
  async assignVehicleToBooking(id, vehicleId, instructions = '') {
    return this.request(`/bookings/bookings/${id}/assign/`, {
      method: 'PATCH',
      body: JSON.stringify({
        vehicle_id: vehicleId,
        instructions: instructions,
      }),
    });
  }

  /**
   * Every vehicle in scope for this booking, each marked available or not.
   *
   * `/fleet/vehicles/` cannot answer this: it knows nothing about the
   * booking's time window, so a dropdown built from it offers vehicles that
   * are in the workshop or already out, and the assignment is then refused
   * with no indication of which vehicle would have worked.
   */
  async getBookingAvailableVehicles(id) {
    return this.request(`/bookings/bookings/${id}/available-vehicles/`);
  }

  /**
   * Start trip for a booking
   */
  async startTripForBooking(id, driverId = null, startOdometer = null) {
    return this.request(`/bookings/bookings/${id}/start_trip/`, {
      method: 'POST',
      body: JSON.stringify({
        driver_id: driverId,
        start_odometer: startOdometer,
      }),
    });
  }

  // ============ Dispatch Management API Methods ============

  /**
   * Get dispatches list
   */
  async getDispatches(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/dispatch/?${queryString}` : '/bookings/dispatch/';
    return this.request(endpoint);
  }

  /**
   * Get dispatch detail
   */
  async getDispatch(id) {
    return this.request(`/bookings/dispatch/${id}/`);
  }

  /**
   * Create dispatch
   */
  async createDispatch(data) {
    return this.request('/bookings/dispatch/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update dispatch
   */
  async updateDispatch(id, data) {
    return this.request(`/bookings/dispatch/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete dispatch
   */
  async deleteDispatch(id) {
    return this.request(`/bookings/dispatch/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Get booking calendar - day view
   */
  async getBookingCalendarDay(date) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/bookings/calendar/day/${params}`);
  }

  /**
   * Get booking calendar - week view
   */
  async getBookingCalendarWeek(date) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/bookings/calendar/week/${params}`);
  }

  /**
   * Get booking calendar - month view
   */
  async getBookingCalendarMonth(date) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/bookings/calendar/month/${params}`);
  }

  /**
   * Get booking dashboard summary
   */
  async getBookingDashboardSummary() {
    return this.request('/bookings/dashboard/summary/');
  }

  /**
   * Get today's bookings
   */
  async getTodayBookings() {
    return this.request('/bookings/dashboard/today/');
  }

  /**
   * Get upcoming bookings
   */
  async getUpcomingBookings(days = 7) {
    return this.request(`/bookings/dashboard/upcoming/?days=${days}`);
  }

  /**
   * Get revenue metrics
   */
  async getRevenueMetrics() {
    return this.request('/bookings/dashboard/revenue/');
  }

  /**
   * Get charts data
   */
  async getChartsData() {
    return this.request('/bookings/dashboard/charts/');
  }

  /**
   * Get KPIs
   */
  async getKPIs() {
    return this.request('/bookings/dashboard/kpis/');
  }

  /**
   * Get trips list
   */
  async getTrips(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/trips/?${queryString}` : '/bookings/trips/';
    return this.request(endpoint);
  }

  /**
   * Get trip detail
   */
  async getTrip(id) {
    return this.request(`/bookings/trips/${id}/`);
  }

  /**
   * Stop a trip
   */
  async stopTrip(id, endOdometer = null, notes = '') {
    return this.request(`/bookings/trips/${id}/stop/`, {
      method: 'POST',
      body: JSON.stringify({
        end_odometer: endOdometer,
        notes: notes,
      }),
    });
  }

  // ============ Customer Management API Methods ============

  /**
   * Get customers list
   */
  async getCustomers(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/finance/ar/customers/?${queryString}` : '/finance/ar/customers/';
    return this.request(endpoint);
  }

  // ============ Location Master API Methods ============

  /**
   * Get locations list
   */
  async getLocations(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/locations/?${queryString}` : '/bookings/locations/';
    return this.request(endpoint);
  }

  /**
   * Get location detail
   */
  async getLocation(id) {
    return this.request(`/bookings/locations/${id}/`);
  }

  /**
   * Create location
   */
  async createLocation(data) {
    return this.request('/bookings/locations/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update location
   */
  async updateLocation(id, data) {
    return this.request(`/bookings/locations/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete location
   */
  async deleteLocation(id) {
    return this.request(`/bookings/locations/${id}/`, {
      method: 'DELETE',
    });
  }

  // ============ Corporate Price List API Methods ============

  /**
   * Get corporate price lists
   */
  async getCorporatePriceLists(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/corporate-price-lists/?${queryString}` : '/bookings/corporate-price-lists/';
    return this.request(endpoint);
  }

  /**
   * Get corporate price list detail
   */
  async getCorporatePriceList(id) {
    return this.request(`/bookings/corporate-price-lists/${id}/`);
  }

  /**
   * Create corporate price list
   */
  async createCorporatePriceList(data) {
    return this.request('/bookings/corporate-price-lists/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update corporate price list
   */
  async updateCorporatePriceList(id, data) {
    return this.request(`/bookings/corporate-price-lists/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete corporate price list
   */
  async deleteCorporatePriceList(id) {
    return this.request(`/bookings/corporate-price-lists/${id}/`, {
      method: 'DELETE',
    });
  }

  // ============ Route Price API Methods ============

  /**
   * Get route prices list
   */
  async getRoutePrices(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/route-prices/?${queryString}` : '/bookings/route-prices/';
    return this.request(endpoint);
  }

  /**
   * Get route price detail
   */
  async getRoutePrice(id) {
    return this.request(`/bookings/route-prices/${id}/`);
  }

  /**
   * Create route price
   */
  async createRoutePrice(data) {
    return this.request('/bookings/route-prices/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update route price
   */
  async updateRoutePrice(id, data) {
    return this.request(`/bookings/route-prices/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete route price
   */
  async deleteRoutePrice(id) {
    return this.request(`/bookings/route-prices/${id}/`, {
      method: 'DELETE',
    });
  }

  // ============ Cost Center Mapping API Methods ============

  /**
   * Get cost center mappings list
   */
  async getCostCenterMappings(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/cost-center-mappings/?${queryString}` : '/bookings/cost-center-mappings/';
    return this.request(endpoint);
  }

  /**
   * Get cost center mapping detail
   */
  async getCostCenterMapping(id) {
    return this.request(`/bookings/cost-center-mappings/${id}/`);
  }

  /**
   * Create cost center mapping
   */
  async createCostCenterMapping(data) {
    return this.request('/bookings/cost-center-mappings/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update cost center mapping
   */
  async updateCostCenterMapping(id, data) {
    return this.request(`/bookings/cost-center-mappings/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete cost center mapping
   */
  async deleteCostCenterMapping(id) {
    return this.request(`/bookings/cost-center-mappings/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * The values the server will accept for a choice field, from DRF's own
   * OPTIONS metadata: [{ value, display_name }].
   *
   * A dropdown that hardcodes its own copy of the model's choices has no way
   * to notice when the model changes. CostCenterMappingForm's Department
   * offered operations/sales/management/other while the model accepted
   * DD/MWD/RNM/SDL/SPERRY-MWD - not one value in common, so every save that
   * touched the field was rejected. Asking the server removes the second copy
   * rather than correcting it and waiting for the next drift.
   *
   * Returns [] when the metadata is unavailable; callers decide what a field
   * with no known choices should do.
   */
  async getFieldChoices(endpoint, field) {
    const metadata = await this.request(endpoint, { method: 'OPTIONS' });
    const actions = metadata?.actions || {};
    // POST describes the writable fields; PUT is present when the caller may
    // edit but not create.
    for (const verb of ['POST', 'PUT']) {
      const choices = actions[verb]?.[field]?.choices;
      if (Array.isArray(choices)) return choices;
    }
    return [];
  }

  // ============ Trip Request Management API Methods ============

  /**
   * Get trip requests list
   */
  async getTripRequests(params = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/bookings/trip-requests/?${queryString}` : '/bookings/trip-requests/';
    return this.request(endpoint);
  }

  /**
   * Download a list export ('csv' | 'xlsx' | 'pdf') applying the SAME
   * filters as the list request.
   *
   * `resource` is the API path of the list, e.g. 'bookings/bookings',
   * 'bookings/trips', 'bookings/dispatch', 'bookings/trip-requests' or
   * 'fleet/vehicles'.
   *
   * Deliberately not `this.request()`: that helper parses JSON, and these
   * responses are binary attachments. The export runs server-side because
   * the lists are paginated - exporting from the browser would only ever
   * capture the page in view.
   */
  async downloadListExport(resource, params = {}, exportFormat = 'csv') {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const queryParams = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        queryParams.append(key, params[key]);
      }
    });
    queryParams.append('export_format', exportFormat);

    const headers = { Authorization: `Bearer ${token}` };
    const tenantId = this.getTenantId();
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }

    const response = await fetch(
      `${API_BASE_URL}/${resource}/export/?${queryParams.toString()}`,
      { headers }
    );

    if (!response.ok) {
      // The server explains refusals (too many rows, unsupported format,
      // portal caller) in JSON - surface that rather than a bare status.
      let detail = `Export failed (${response.status})`;
      try {
        const body = await response.json();
        if (body && body.detail) detail = body.detail;
      } catch (error) {
        /* non-JSON error body; keep the status message */
      }
      throw new Error(detail);
    }

    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const fallback = `${resource.split('/').pop()}.${exportFormat}`;
    return { blob, filename: match ? match[1] : fallback };
  }

  /**
   * Get trip request detail
   */
  async getTripRequest(id) {
    return this.request(`/bookings/trip-requests/${id}/`);
  }

  /**
   * Create trip request
   */
  async createTripRequest(data) {
    return this.request('/bookings/trip-requests/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update trip request
   */
  async updateTripRequest(id, data) {
    return this.request(`/bookings/trip-requests/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete trip request
   */
  async deleteTripRequest(id) {
    return this.request(`/bookings/trip-requests/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Convert trip request to booking
   */
  /**
   * Convert a trip request to a booking.
   *
   * The endpoint requires pricing_plan_id for direct customers - a corporate
   * one is priced from its route price list, a direct one has nothing to
   * price from. This used to send no body at all, so a direct customer's
   * request could never be converted from the UI.
   */
  async convertTripRequestToBooking(id, data = {}) {
    return this.request(`/bookings/trip-requests/${id}/convert_to_booking/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Which corporate fields this customer must supply.
   *
   * Drives what the form shows. The server resolves policy again on save, so
   * this response is a convenience, never the authority.
   */
  async getTripRequestFieldRequirements(customerId) {
    const query = customerId ? `?customer_id=${encodeURIComponent(customerId)}` : '';
    return this.request(`/bookings/trip-requests/field-requirements/${query}`);
  }

  /**
   * Structured capacity requirement for the later assignment stage.
   */
  async getTripRequestCapacitySummary(id) {
    return this.request(`/bookings/trip-requests/${id}/capacity-summary/`);
  }

  /**
   * Record an authorised acknowledgement of a timing warning.
   */
  async acknowledgeTripRequestWarning(id, code, reason) {
    return this.request(`/bookings/trip-requests/${id}/acknowledge-warning/`, {
      method: 'POST',
      body: JSON.stringify({ code, reason }),
    });
  }
}

// Export singleton instance
export const apiClient = new AdminAPIClient();
export default apiClient;

// Exported for tests, and for any caller that needs to render an error body
// it already holds.
export { describeApiError, fieldErrorsFrom };

